import { faBan, faThumbsUp } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { fillZero } from "app/tools/common"
import { getFormattedFileSize } from "app/tools/file"
import { dateSVC } from "app/util/date/service"
import { CAMPAIGN_LANGUAGES } from "campaign/model"
import { formatDuration, intervalToDuration } from "date-fns"
import { MediaType } from "media/components/MediaType"
import {
  IMediaVideo,
  ISspMediaApproval,
  ISspMediaApprovalFilters,
  ISspMediaApprovalInfo,
  ISspMediaApprovalsByMedia,
  MEDIA_APPROVAL_STATUSES,
  MEDIA_SIMPLE_TYPES
} from "media/model"
import { mediaRPO } from "media/repository"
import { mediaActionCreator } from "media/store/actions"
import { getDefaultMediaApprovalFilters } from "media/store/state"
import { messageSVC } from "message/service"
import { normalizeSync } from "normalize-diacritics"
import React from "react"
import { IActionButton } from "ui/model"
import { getMediaType } from "./common"

export const loadAllMediaApprovals = async () => {
  const newMediaApprovals = await mediaRPO.getAllMediaApprovals()
  getStore().dispatch(mediaActionCreator.setMediaApprovalList(newMediaApprovals))
}

export const setMediaApprobationFilterPanelExpansion = (isFilterExpanded: boolean) => {
  getStore().dispatch(mediaActionCreator.setFilterExpansion(isFilterExpanded))
}

export const getMediaApprovalActiveFilterCount = (filters: ISspMediaApprovalFilters): number => {
  let activeFilterCount = 0
  if (filters.approvalStatuses.length !== 0) {
    activeFilterCount++
  }
  if (filters.languages.length !== 0) {
    activeFilterCount++
  }
  return activeFilterCount
}

export const setMediaApprovalFilters = (filters?: Partial<ISspMediaApprovalFilters>) => {
  const store = getStore()
  let allMediaApprovals = store.getState().media.sspMediaApproval.mediaApprovalsData.slice()
  if (!filters) {
    const newFilters = getDefaultMediaApprovalFilters()
    store.dispatch(mediaActionCreator.setMediaApprovalFilters(allMediaApprovals, newFilters))
    return
  }
  const newFilters = {
    ...store.getState().media.sspMediaApproval.filters,
    ...filters
  }
  const normalizedNameFilter = newFilters.name ? normalizeSync(newFilters.name).toLowerCase() : ""
  const filteredMediaApprovals = allMediaApprovals.filter(m => {
    if (normalizedNameFilter && !normalizeSync(m.name || "").includes(normalizedNameFilter)) {
      return false
    }
    if (
      newFilters.createdAt &&
      newFilters.createdAt.date &&
      m.created_at &&
      new Date(m.created_at) < newFilters.createdAt.date
    ) {
      return false
    }
    if (newFilters.approvalStatuses.length > 0) {
      if (
        !m.mediaApprovalsByMedia.ssp_approval.find(a =>
          newFilters.approvalStatuses.includes(a.approval_status_id as MEDIA_APPROVAL_STATUSES)
        )
      ) {
        return false
      }
    }
    if (newFilters.languages.length > 0) {
      if (
        !m.mediaApprovalsByMedia.ssp_approval.find(a => newFilters.languages.includes(a.language as CAMPAIGN_LANGUAGES))
      ) {
        return false
      }
    }
    return true
  })
  store.dispatch(mediaActionCreator.setMediaApprovalFilters(filteredMediaApprovals, newFilters))
}

export const clearMediaApprobationFilters = () => {
  setMediaApprovalFilters()
}

export const getMediaVideos = (mediaId: number): Promise<IMediaVideo[]> => mediaRPO.getMediaVideos(mediaId)

export const updateMediaApproval = (
  mediaApproval: ISspMediaApproval,
  status: MEDIA_APPROVAL_STATUSES,
  noConfirmation?: boolean
): Promise<ISspMediaApproval | null> => {
  return new Promise<ISspMediaApproval | null>(resolve => {
    if (noConfirmation) {
      return resolve(processUpdateMediaApproval(mediaApproval, status))
    }
    messageSVC.confirmMessage({
      message: translate("media-approval.update.message"),
      title: translate(`media-approval.${status}.title`),
      onSuccess: async () => {
        resolve(processUpdateMediaApproval(mediaApproval, status))
      },
      onCancel: () => resolve(null)
    })
  })
}

export const getMediaApprovalInfos = (mediaApprovalsByMedia: ISspMediaApprovalsByMedia): ISspMediaApprovalInfo[] => {
  const media = mediaApprovalsByMedia.media
  const infos: ISspMediaApprovalInfo[] = []
  const type = getMediaType(media)
  infos.push({
    label: translate("label.type"),
    value: <MediaType type={getMediaType(media)} />
  })
  infos.push({
    label: translate("label.creationDate"),
    value: media.created_at ? dateSVC.format(media.created_at) : "-"
  })
  infos.push({
    label: translate("label.language"),
    value:
      mediaApprovalsByMedia.ssp_approval && mediaApprovalsByMedia.ssp_approval.length > 0
        ? mediaApprovalsByMedia.ssp_approval[0].language
        : "-"
  })
  infos.push({
    label: translate("label.size"),
    value: media.filesize ? getFormattedFileSize(media.filesize, 2) : "-"
  })
  if (type === MEDIA_SIMPLE_TYPES.IMAGE) {
    infos.push({
      label: translate("label.dimensions"),
      value: `${media.width}x${media.height}px`
    })
    infos.push({
      label: translate("label.aspect-ratio"),
      value: !media.height ? 0 : `${((media.width || 0) / media.height).toFixed(2)}`
    })
  } else if (media.media_video && media.media_video.length > 0) {
    const video = media.media_video[0]
    infos.push({
      label: translate("label.dimensions"),
      value: `${video.width}x${video.height}px`
    })
    infos.push({
      label: translate("label.bitrate"),
      value: `${video.bitrate}kbps`
    })
    const duration = intervalToDuration({ start: 0, end: (video.duration || 0) * 1000 })
    const formattedDuration = formatDuration(duration, {
      format: ["minutes", "seconds"],
      zero: true,
      delimiter: ":",
      locale: {
        formatDistance: (_token, count) => fillZero(count)
      }
    })
    infos.push({
      label: translate("label.duration"),
      value: formattedDuration
    })
    infos.push({
      label: translate("label.aspect-ratio"),
      value: !video.height ? 0 : `${((video.width || 0) / video.height).toFixed(2)}`
    })
  }
  return infos
}

const processUpdateMediaApproval = async (
  mediaApproval: ISspMediaApproval,
  status: MEDIA_APPROVAL_STATUSES
): Promise<ISspMediaApproval | null> => {
  try {
    getStore().dispatch(mediaActionCreator.startUpdatingMediaApproval())
    const updatedMediaApproval = await mediaRPO.updateMediaApproval(mediaApproval, status)
    getStore().dispatch(mediaActionCreator.updateMediaApproval(updatedMediaApproval))
    return updatedMediaApproval
  } catch (error) {
    console.error("Unable to update media approval", error)
    getStore().dispatch(mediaActionCreator.stopUpdatingMediaApproval())
    messageSVC.error(translate("media-approval.update.error"))
    return null
  }
}

export const getPreviewUrl = (mediaId: number): string => mediaRPO.getMediaPreviewUrl(mediaId)

export const getMediaApprovalButtons = (
  mediaApproval: ISspMediaApproval,
  setIsUpdating: (isUpdating: boolean) => any
): IActionButton[] => {
  const approveButton: IActionButton = {
    label: translate("media-approval.approve"),
    icon: faThumbsUp,
    onClick: async () => {
      setIsUpdating(true)
      await updateMediaApproval(mediaApproval, MEDIA_APPROVAL_STATUSES.APPROVED, true)
      setIsUpdating(false)
    }
  }
  const rejectButton = {
    label: translate("media-approval.reject"),
    icon: faBan,
    onClick: async () => {
      setIsUpdating(true)
      await updateMediaApproval(mediaApproval, MEDIA_APPROVAL_STATUSES.REFUSED, true)
      setIsUpdating(false)
    }
  }
  const pendingButton = {
    label: translate("media-approval.pending"),
    icon: faBan,
    onClick: async () => {
      setIsUpdating(true)
      await updateMediaApproval(mediaApproval, MEDIA_APPROVAL_STATUSES.PENDING, true)
      setIsUpdating(false)
    }
  }
  switch (mediaApproval.approval_status_id) {
    case MEDIA_APPROVAL_STATUSES.REFUSED: {
      return [approveButton, pendingButton]
    }
    case MEDIA_APPROVAL_STATUSES.PENDING: {
      return [approveButton, rejectButton]
    }
    case MEDIA_APPROVAL_STATUSES.APPROVED: {
      return [rejectButton, pendingButton]
    }
  }
}
